<template>
    <BaseInput
        v-bind="$attrs"
        :model-value="localModel"
        :label="label"
        :name="name || label">
        <Combobox
            :model-value="localModel"
            :multiple="multiple"
            :disabled="inputDisabled"
            :nullable="nullable"
            as="div"
            @update:model-value="handleSelection">
            <div class="flex items-center justify-between">
                <div
                    v-if="$slots.inputHelper"
                    class="text-sm">
                    <slot name="inputHelper" />
                </div>
            </div>
            <div class="relative">
                <ComboboxButton
                    as="div"
                    class="min-h-9 flex flex-wrap py-1.5 px-3 pr-10 group/select w-full rounded-md border-0 ring-1 ring-inset after:content-[attr(placeholder)] after:absolute"
                    :class="[inputDisabled ? 'cursor-not-allowed bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 ring-gray-200 shadow-none dark:ring-gray-400' : 'ring-gray-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-300 shadow-xs focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-500']"
                    @focus="inputHandler.focus()">
                    <div class="flex gap-1 w-full flex-wrap items-center">
                        <!-- Multi Select Options List -->
                        <template
                            v-if="multiple && selectedOption?.length > 0">
                            <div
                                v-for="option in selectedOption"
                                :key="_identity(option)">
                                <div
                                    :title="_display(option)"
                                    :class="inputDisabled ? 'outline-gray-200 dark:outline-gray-400 dark:bg-gray-700' : 'outline-gray-300 dark:outline-gray-300 dark:bg-gray-800'"
                                    class="z-8 truncate max-w-32 flex gap-1 items-center outline outline-1 rounded-full w-fit">
                                    <span
                                        class="text-xs pl-2 truncate"
                                        :class="{'text-gray-500 dark:text-gray-400 cursor-not-allowed' : inputDisabled}">
                                        {{ _display(option) }}
                                        <span
                                            v-if="_additional(option)"
                                            class="text-xs text-gray-400">
                                            <br>{{ _additional(option) }}
                                        </span>
                                    </span>
                                    <button
                                        :disabled="inputDisabled"
                                        class="flex items-center content-center bg-gray-200 dark:bg-gray-600 p-1 m-1 rounded-full hover:cursor-pointer disabled:cursor-not-allowed enabled:hover:bg-gray-300 dark:enabled:hover:bg-gray-500"
                                        @focus="inputHandler.focus()"
                                        @click.prevent.stop="removeSelectedItem(_identity(option))">
                                        <Icon
                                            class="size-2"
                                            name="heroicons:x-mark-20-solid" />
                                    </button>
                                </div>
                            </div>
                        </template>

                        <!-- Filter/Search Query Input -->
                        <ComboboxInput
                            ref="input"
                            :disabled="disabled"
                            class="p-0 grow max-w-full sm:text-sm sm:leading-6 border-none dark:border-none bg-transparent dark:bg-transparent focus:ring-0 disabled:cursor-not-allowed"
                            :class="[(!multiple && localModel) ? 'absolute inset-0 w-full h-full text-transparent dark:text-transparent placeholder-transparent focus:relative focus:text-gray-500 dark:focus:text-gray-400' : '', !showInput ? 'hidden' : '', inputOverridden ? 'hidden group-focus/select:block group-focus-within/select:block' : '']"
                            :placeholder="currentPlaceholder"
                            :display-value="() => ''"
                            @focus="openOptions = true"
                            @change="onType($event.target.value)"
                            @blur="handleBlur" />

                        <!-- Focus helper -->
                        <input
                            v-if="!multiple && selectedOption"
                            ref="selectedOptionFocus"
                            class="absolute inset-0 w-full h-full -z-10 opacity-0 block"
                            @blur="showInput = true">

                        <!-- Single Select Option (Display) -->
                        <div
                            v-if="!multiple && selectedOption"
                            class="p-0 grow max-w-full sm:text-sm sm:leading-6 border-none dark:border-none bg-transparent dark:bg-transparent disabled:cursor-not-allowed"
                            :class="[showInput && !inputDisabled ? 'block group-focus-within/select:hidden' : '']">
                            <slot
                                v-bind="selectedOption"
                                name="selectedOption">
                                <span :class="[{'truncate' : truncate}]">
                                    {{ _display(selectedOption) }}
                                </span>
                                <span
                                    v-if="_additional(selectedOption)"
                                    class="text-xs text-gray-400"
                                    :class="[{'truncate' : truncate}]">
                                    <br>{{ _additional(selectedOption) }}
                                </span>
                            </slot>
                        </div>
                    </div>

                    <!-- Additional Input Options-->
                    <div
                        v-if="nullable && model && !inputDisabled"
                        class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden cursor-pointer"
                        @click="inputHandler.clear()">
                        <Icon
                            name="heroicons:x-circle-20-solid"
                            class="text-gray-500 size-5"
                            aria-hidden="true" />
                    </div>
                    <div
                        v-else-if="!inputDisabled && inSpaSearchMode"
                        class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden cursor-pointer">
                        <Icon
                            name="heroicons:magnifying-glass-20-solid"
                            class="size-5"
                            color="text-gray-400"
                            aria-hidden="true" />
                    </div>
                    <div
                        v-else-if="!inputDisabled"
                        class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden cursor-pointer">
                        <Icon
                            name="heroicons:chevron-up-down-20-solid"
                            class="size-5"
                            color="text-gray-400"
                            aria-hidden="true" />
                    </div>
                </ComboboxButton>

                <ComboboxOptions
                    class="absolute z-[1001] mt-1  max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
                    :static="openOptions">
                    <!-- Create Custom Option -->
                    <BaseOption
                        v-if="allowCustom"
                        :class="!query?.length ? 'text-xs' : 'font-semibold'"
                        :label="query?.length ? 'Neu: ' + query : 'Wert eingeben und Enter drücken'"
                        :disabled="!query?.length"
                        :truncate="truncate"
                        :value="_identity(queryOption)" />

                    <!-- Load Remote Options-->
                    <BaseOption
                        v-if="pending"
                        label=""
                        disabled>
                        <li class="flex justify-center overflow-hidden">
                            <LoadingSpinner size="md" />
                        </li>
                    </BaseOption>

                    <!-- No Results From Filtering -->
                    <BaseOption
                        v-else-if="!allowCustom && !filteredOptions?.length"
                        label=""
                        disabled
                        as="template">
                        <slot name="noResults">
                            {{ noResults }}
                        </slot>
                    </BaseOption>

                    <slot
                        v-else
                        v-bind="{options: groupedOptions || filteredOptions}"
                        name="options">
                        <!-- Grouped Options -->
                        <template v-if="groupedOptions">
                            <template
                                v-for="(optionGroup, category) in groupedOptions"
                                :key="category">
                                <li class="sticky top-0 z-[1001] cursor-default select-none py-2 pl-3 bg-gray-50 pr-9 text-xs text-gray-500">
                                    {{ category }}
                                </li>
                                <BaseOption
                                    v-for="option in optionGroup"
                                    :key="_identity(option)"
                                    :disabled="inputDisabled"
                                    :value="_identity(option)"
                                    :label="_display(option)" />
                            </template>
                        </template>

                        <!-- Non Grouped Options -->
                        <template v-else>
                            <BaseOption
                                v-for="option in filteredOptions"
                                :key="_identity(option)"
                                :disabled="_disabled(option)"
                                :value="_identity(option)"
                                :truncate="truncate"
                                :label="_display(option)"
                                :additional="_additional(option)">
                                <template v-if="$slots.option">
                                    <slot
                                        name="option"
                                        v-bind="{option}" />
                                </template>
                            </BaseOption>
                        </template>
                    </slot>
                </ComboboxOptions>
            </div>
        </Combobox>
    </BaseInput>
</template>
<script setup>
import {cloneDeep, groupBy as grouped, isEqual, uniqBy} from 'lodash'
import debounce from "lodash/debounce"
import {useNuxtApp} from "nuxt/app"
import {computed, ref} from 'vue'
import BaseOption from '@/components/global/Inputs/BaseOption'

const {t: $t} = useI18n()
const notification = useNotification()

const props = defineProps({
    //field
    modelValue: {
        type: [Number, String, Array, Object, Boolean, null],
        default: null,
        description: 'The selected option'
    },
    label: {
        required: false,
        type: String,
        default: null,
        description: 'The label of the select'
    },
    name: {
        required: false,
        type: String,
        default: null,
        description: 'The name of the element, required for validation if no label is provided.'
    },
    disabled: {
        type: Boolean,
        default: false,
        description: 'If the select is disabled'
    },
    placeholder: {
        required: false,
        type: String,
        default: '',
        description: 'The placeholder text of the search input'
    },
    multiple: {
        type: Boolean,
        default: false,
        description: 'Whether the select should be a multi select'
    },
    nullable: {
        type: Boolean,
        default: false,
        description: 'Whether the select should be nullable'
    },

    //options
    options: {
        required: false,
        type: Array,
        default: () => [],
        description: 'The options to display in the select if no api route is given'
    },
    identifier: {
        required: false,
        type: String,
        default: 'value',
        description: 'The key of the value that should be saved in the modelValue'
    },
    displayKey: {
        required: false,
        type: String,
        default: 'label',
        description: 'The key of the value to display in the options'
    },
    displayFormat: {
        type: Function,
        default: null,
        description: 'A function that formats the display value of the options, eg. (option) => option.label + " (" + option.id + ")".'
    },
    additionalDisplayKey: {
        type: String,
        default: null,
        description: 'An additional key of a value to display as a subtext in the options, eg. an id'
    },
    disabledKey: {
        type: String,
        default: 'disabled',
        description: 'The key of the option that handles if the option is disabled'
    },
    groupBy: {
        type: [String, Function],
        default: null,
        description: 'The key of the value to group the options by'
    },
    truncate: {
        type: Boolean,
        default: false,
        description: 'Truncate the text of the options if they are too long'
    },
    allowCustom: {
        type: Boolean,
        default: false,
        description: 'If the component should allow arbitrary values'
    },
    defaultFirstOption: {
        type: Boolean,
        default: false,
        description: 'If the component should select the first option by default'
    },
    noResults: {
        required: false,
        type: String,
        default: () => useI18n().t('noResults'),
        description: 'The text to display if no results are found'
    },

    //remote search
    spaRoute: {
        required: false,
        type: [String, Function],
        default: null,
        description: 'The spa route to fetch the options from'
    },
    spaProps: {
        type: Boolean,
        default: false,
        description: 'If the component should use the default props for the api route (id, name, additional)'
    },
    spaPrefetch: {
        type: Boolean,
        default: false,
        description: 'If the component should prefetch the options on mount'
    },
    spaFilter: {
        required: false,
        type: Array,
        default: () => [],
        description: 'The filter to apply to the api route'
    },
    spaParams: {
        type: Object,
        default: () => ({}),
        description: 'The params to send to the api route'
    },
    spaRouteParams: {
        type: Object,
        default: () => ({}),
        description: 'The params to send to the api route'
    },
    searchThreshold: {
        required: false,
        type: Number,
        default: 2,
        description: 'The threshold of the search input'
    }
})

const emits = defineEmits(['update:modelValue', 'update:entireObject'])
const input = ref(null)
const abortController = ref(new AbortController())

const model = computed({
    get: () => props.modelValue || (props.multiple ? [] : null),
    set: (value) => emits('update:modelValue', value)
})

const {localModel} = useLocalModel(model)

const query = ref('')
const searchOptions = ref([])

// const selectedOption = ref(props.multiple ? [] : null) // deprecated, use computed

const selectedOptionFocus = ref(null)
const openOptions = ref(false)
const showInput = ref(true)
const pending = ref(false)

const inputOverridden = computed(() => props.multiple && !!localModel.value?.length)
const queryOption = computed(() => ({
    [identifierProp.value]: query.value,
    [props.displayKey]: query.value
}))

const inSpaSearchMode = computed(() => !!props.spaRoute && !props.spaPrefetch)
const inputDisabled = computed(() => props.disabled || initializing.value)
const identifierProp = computed(() => props.spaProps ? 'id' : props.identifier)
const displayProp = computed(() => props.spaProps ? 'name' : props.displayKey)
const additionalDisplayProp = computed(() => props.additionalDisplayKey)

const currentPlaceholder = computed(() => {
    if (inputOverridden.value) {
        return ''
    } else if (props.placeholder) {
        return props.placeholder
    } else if (inSpaSearchMode.value) {
        return 'Suche nach Name...'
    } else {
        return useI18n().t('pleaseSelect')
    }
})

// Makes sure that internally all options are objects
function _asOption(option) {
    if (!option) return null
    return {
        ...(typeof option === 'object' ? option : {}),
        [identifierProp.value]: option[identifierProp.value] ?? option?.id ?? option,
        [displayProp.value]: option[displayProp.value] ?? option?.name ?? option
    }
}

function _identity(option) {
    return _get(option, identifierProp.value) ?? option
}

function _display(option) {
    if (!option) return null
    if (props.displayFormat) return props.displayFormat(option)
    return _get(option, displayProp.value) ?? _identity(option)
}

function _additional(option) {
    return _get(option, additionalDisplayProp.value) ?? null
}

function _disabled(option) {
    return _get(option, props.disabledKey) ?? false
}

function _get(option, prop) {
    if (!option) return null
    return option[prop] ?? null
}

const fixedOptions = computed(() => {
    // make all options objects
    return (props.options ?? []).map(_asOption)
})

const _selectableOptions = computed(() => {
    const allOptions = [...cloneDeep(fixedOptions.value), ...cloneDeep(searchOptions.value)]
    return uniqBy(allOptions, (option) => _identity(option))
})

const _customOptions = computed(() => {
    // all localModel.value options, that are not in the selectableOptions
    const localValue = Array.isArray(localModel.value) ? localModel.value : (localModel.value ? [localModel.value] : [])

    const customOptions = localValue.filter((option) => {
        return !_selectableOptions.value.find((opt) => _identity(opt) === _identity(option))
    }) ?? []

    return customOptions.map((option) => {
        return {
            [identifierProp.value]: option,
            [displayProp.value]: option,
            arbitrary: true
        }
    })
})

// all options, including custom options
const allOptions = computed(() => {
    const allOptions = [...cloneDeep(_selectableOptions.value), ...cloneDeep(_customOptions.value)]
    return uniqBy(allOptions, (option) => _identity(option))
})

// the internal object representation of the selected option
const selectedOption = computed(() => {
    if (props.multiple) {
        return localModel.value?.map((option) => {
            return allOptions.value.find((opt) => _identity(opt) === _identity(option)) ?? option
        })
    } else {
        return allOptions.value.find((opt) => _identity(opt) === _identity(localModel.value)) ?? localModel.value
    }
})

const filteredOptions = computed(() => {
    if (inSpaSearchMode.value || !query.value?.length) return allOptions.value

    return allOptions.value.filter((option) => {
        return _display(option)?.toLowerCase().includes(query.value.toLowerCase()) || (props.additionalDisplayKey && _additional(option)?.toLowerCase().includes(query.value.toLowerCase()))
    })
})

const groupedOptions = computed(() => {
    if (!props.groupBy) return false
    return grouped(cloneDeep(filteredOptions.value), props.groupBy)
})

function handleSelection(selection) {
    updateSelection(selection)
    if (props.multiple) {
        input.value.el.value = ''
        onType('')
    } else {
        openOptions.value = false
        inputHandler.blur()
    }
    updateSelection()
}

function removeSelectedItem(itemId) {
    const index = localModel.value.findIndex((option) => _identity(option) === itemId)
    localModel.value.splice(index, 1)
}

function updateSelection(value = null) {
    if (!value) value = localModel.value
    if (value) value = _identity(value)

    if (!props.multiple) {
        localModel.value = value ?? null

        if (!value && props.defaultFirstOption && !!allOptions.value?.length) {
            localModel.value = _identity(allOptions.value[0])
        } else {
            localModel.value = value
        }
    } else {
        if (value === null) value = []

        if (props.defaultFirstOption && value?.length === 0 && allOptions.value.length > 0) {
            value.push(_identity(allOptions.value[0]))
        }

        localModel.value = value ?? []
    }

    return localModel.value
}

const onType = (input) => {
    query.value = input
    onSearch.cancel()
    pending.value = false

    if (!inSpaSearchMode.value) return
    if (query.value.length <= props.searchThreshold) return

    abortController.value?.abort()
    pending.value = true
    onSearch()
}

const onSearch = debounce(() => {
    fetchOptions(query.value, props.spaFilter, props.spaParams)
}, 500)

onMounted(async () => {
    // make sure all props are initialized
    await nextTick()

    if (props.spaRoute) {
        initializeSpaSearch()
    } else {
        updateSelection()
    }
})

//////////////////
// SPA FEATURES //
//////////////////

const initializing = ref(false)

async function initializeSpaSearch() {
    initializing.value = true
    if (props.spaPrefetch) {
        await fetchOptions(query.value, props.spaFilter, props.spaParams)
    } else {
        if (props.multiple && localModel.value?.length || !props.multiple && localModel.value) {
            const filter = [
                ...props.spaFilter,
                {
                    name: 'customFilterFind',
                    column: 'customFilterFind',
                    operator: 'find',
                    value: localModel.value
                }
            ]
            await fetchOptions(query.value, filter, props.spaParams)
        } else {
            updateSelection()
        }
    }

    initializing.value = false
}

async function fetchOptions(search, filter, params) {
    pending.value = true

    if (typeof props.spaRoute !== 'string') {
        try {
            const response = await props.spaRoute(search)
            pending.value = false
            setOptions(response)
            updateSelection()
            return
        } catch {
            pending.value = false
            return
        }
    }


    abortController.value = new AbortController()

    const response = await $lara.get(useNuxtApp().$apiRoute(props.spaRoute, props.spaRouteParams), {
        method: 'GET',
        abortController: abortController.value,
        params: {
            search: search,
            filter: filter,
            ...params
        }
    })

    pending.value = false

    if (response?.error) {
        if (response.error?.response?.status === 422) {
            notification.error($t('invalidRequestData'))
        } else if (response.error?.response?.status === 404) {
            notification.error($t('modelDoesNotExist'))
        }
        return
    }

    setOptions(response.data)
    updateSelection()
}

function setOptions(options = []) {
    const newOptions = cloneDeep(options ?? [])
    searchOptions.value.forEach((option) => {
        if (newOptions.some((newOption) => _identity(newOption) === _identity(option))) return
        if (Array.isArray(localModel.value) && localModel.value.includes(_identity(option))) {
            newOptions.push(option)
        } else if (!Array.isArray(localModel.value) && localModel.value === _identity(option)) {
            newOptions.push(option)
        }
    })
    searchOptions.value = newOptions
}

function handleBlur() {
    openOptions.value = false

    if (localModel.value === null) {
        inputHandler.clear()
    } else {
        onType('')
        updateSelection()
    }

    if (!props.multiple && localModel.value) {
        showInput.value = false
        selectedOptionFocus.value?.focus()
    }
}

const inputHandler = {
    focus: () => {
        showInput.value = true
        input.value.el.focus()
    },
    blur: () => {
        if (!props.multiple) input.value.el.blur()
    },
    clear: () => {
        showInput.value = true
        model.value = null
        input.value.el.value = ''
        onType('')
    }
}

// refetch options in filter change
watch(() => props.spaFilter, (newVal, oldVal) => {
    if (!isEqual(newVal, oldVal) && props.spaRoute && props.spaPrefetch) {
        fetchOptions(query.value, props.spaFilter, props.spaParams)
    }
}, {deep: true})

watch(() => selectedOption.value, (value) => {
    emits('update:entireObject', value ?? null)
})

watch(() => localModel.value, () => {
    if (!props.multiple) {
        if (!localModel.value && props.defaultFirstOption && !!allOptions.value?.length) {
            localModel.value = _identity(allOptions.value[0])
        }
        if (!localModel.value) {
            showInput.value = true
        }
    } else {
        if (!localModel.value?.length && props.defaultFirstOption && allOptions.value.length > 0) {
            localModel.value = [_identity(allOptions.value[0])]
            if (!localModel.value?.length) {
                showInput.value = true
            }
        }
    }
}, {immediate: true})

</script>